import { Component, OnInit, ViewChild, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { CardComponent } from '@ppl/ui';
import { UserService } from '../../services/user.service';
import { AuthService } from '../../services/auth.service';
import { Program, Role, UserDataResponse, UserDetails } from '../../interfaces/user.interface';
import { MenuService } from '../../services/menu.service';
import { environment } from '../../../environments/environment';
import { ReferralDataService } from '../../services/referral-data.service';
import { ENROLLMENT_TITLE_MAPPINGS, ENROLLMENT_TYPE_BACKEND_MAPPINGS, STATIC_DATA } from '../../shared/constants/static-data.constants';
import { MENU_BASE_URLS } from '../../shared/constants/url.constants';
import { LeadDetailsService } from '../../services/lead-details.service';
import { SectionsService } from '../../services/sections.service';
import { LookupService } from '../../services/lookup.service';
import { MainMenuItem } from '../../interfaces/menu.interface';
import { StorageService } from '../../services/storage.service';
import { Popover } from 'primeng/popover';
import { AllowListAuthStore } from '../../shared/authorization/allow-list-auth.store';

@Component({
  selector: 'ppl-dashboard',
  standalone: true,
  imports: [CommonModule, CardComponent, Popover, RouterModule],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.css'
})
export class DashboardComponent implements OnInit {
  userPrograms!: Program[];
  displayPrograms: any[] = [];
  userData!: UserDetails;
  savedData: any;
  isPreLaunch = false;
  // Date shown to users on the pre-launch banner. Hard-coded intentionally so it can
  // differ from environment.goLiveDate (the logic cutoff, kept later as a safety buffer).
  goLiveDisplayDate = 'July 1, 2026';
  private isWithinPreLaunchWindow = false;

  @ViewChild('rolePopover') rolePopover!: Popover;
  
  pendingSelection: {
    program: Program;
    roles: Role[];
  } | null = null;

  constructor(
    private userService: UserService,
    private authService: AuthService,
    private router: Router,
    private menuService: MenuService,
    private referralDataService: ReferralDataService,
    private leadDetailsService: LeadDetailsService,
    private sectionService: SectionsService,
    private lookupService: LookupService,
    private storageService: StorageService,
    private cdr: ChangeDetectorRef,
    private allowListStore: AllowListAuthStore
  ) { }

  ngOnInit(): void {
    if (environment.showGoLiveBanner && environment.goLiveDate) {
      const goLive = new Date(environment.goLiveDate + 'T00:00:00');
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      this.isWithinPreLaunchWindow = today < goLive;
    }

    this.authService.getUsers().subscribe(
      (data: UserDataResponse) => {
        this.userService.setUserDetails(data.responsedata);
        this.userData = data.responsedata;
        // Show the pre-launch banner for everyone in the window except excluded accounts.
        this.isPreLaunch = this.isWithinPreLaunchWindow && !this.isPreLaunchExcludedUser(data.responsedata);
        this.userPrograms = data.responsedata.programs?.filter(program => program.program_state.toLocaleLowerCase() !== 'internal');
        // Transform programs to match ppl-ui card format
        this.displayPrograms = this.userPrograms.map(program => ({
          ...program,
          name: program.program_name
        }));
        this.userService.setUserPrograms(this.userPrograms);

        if (this.userPrograms.length === 1) {
          this.onProgramSelection(this.userPrograms[0], null);
        }
      },
      (error) => {
        console.error(error);
      }
    );


    ///this.userService.programSubject.subscribe((data) => this.userPrograms = data);
  }


  /**
   * Accounts whose email/username start with this prefix bypass the pre-launch
   * banner (early-access / test accounts created ahead of go-live).
   */
  private static readonly PRE_LAUNCH_EXCLUDED_PREFIX = 'pplmya2023';

  /**
   * Accounts whose email is on this domain (PPL staff) also bypass the
   * pre-launch banner.
   */
  private static readonly PRE_LAUNCH_EXCLUDED_DOMAIN = '@pplfirst.com';

  private isPreLaunchExcludedUser(user: UserDetails): boolean {
    const prefix = DashboardComponent.PRE_LAUNCH_EXCLUDED_PREFIX;
    const domain = DashboardComponent.PRE_LAUNCH_EXCLUDED_DOMAIN;
    const candidates = [user?.email, user?.username];
    return candidates.some(value => {
      if (!value) return false;
      const normalized = value.trim().toLowerCase();
      return normalized.startsWith(prefix) || normalized.endsWith(domain);
    });
  }

  onProgramSelection(program: Program, event: MouseEvent | null): void {
    if (this.isPreLaunch) return;
    const roles = this.getValidRoles(program);
    this.userService.removeSelectedRole();
    this.storageService.clearStorage();

    if (this.shouldAutoNavigate(roles)) {
      this.navigateWithSelection(program, roles[0]);
      return;
    }

    if (event) {
      this.showRoleSelectionPopover(program, roles, event);
    }
  }

  onRoleSelection(role: Role): void {
    if (!this.pendingSelection) return;
    this.userService.removeSelectedRole();
    this.navigateWithSelection(this.pendingSelection.program, role);
    this.rolePopover.hide();
    this.clearPendingSelection();
  }

  getDisplayRoleName(role: Role): string {
    const roles = this.pendingSelection?.roles || [];
    const hasProvider = roles.some(r => r.role_name === 'Provider');
    const hasParticipant = roles.some(r => r.role_name === 'Participant');
    if (hasProvider && hasParticipant && role.role_name === 'Provider') {
      return 'Employer';
    }
    return role.role_name;
  }

  private getValidRoles(program: Program): Role[] {
    // PRODUCTION: Use this line
    return (program.roles || []).filter(Boolean);
    
    // TESTING: Add dummy roles to test popover
    // const testRoles: Role[] = [
    //   { role_name: 'PPL Staff', role_sid: 2, roleHierarchy: 4 },
    //   { role_name: 'Program Manager', role_sid: 3, roleHierarchy: 3 },
    //   { role_name: 'Data Entry Clerk', role_sid: 4, roleHierarchy: 2 }
    // ];
    // return [...(program.roles || []).filter(Boolean), ...testRoles];
  }

  private shouldAutoNavigate(roles: Role[]): boolean {
    return roles.length <= 1;
  }

  private showRoleSelectionPopover(program: Program, roles: Role[], event: MouseEvent): void {
    this.pendingSelection = { program, roles };
    this.rolePopover.hide();
    setTimeout(() => {
      this.rolePopover.show(event);
      this.cdr.markForCheck();
    }, 0);
  }

  private clearPendingSelection(): void {
    this.pendingSelection = null;
    this.cdr.markForCheck();
  }

  private navigateWithSelection(program: Program, role?: Role): void {
    this.userService.removeSelectedRole();
    this.userService.setSelectedProgram(program);

    if (role) {
      this.userService.setSelectedRole(role);
    }

    this.sectionService.clearTab();

    // --- Load allow-list config for the selected role ---
    if (role?.role_sid) {
      this.allowListStore.loadConfig(role.role_sid, program.program_code).subscribe(config => {
        if (config) {
          const allowedMenuIds = this.allowListStore.getAllowedMenuIds();
          this.userService.setAllowedMenus(allowedMenuIds);

          if (allowedMenuIds.length === 0) {
            // No menus allowed for this role — show error page
            this.router.navigate(['/unauthorized']);
            return;
          }

          const menuItemstoDisplay = this.menuService.getAllowedMenus(program.program_code, allowedMenuIds);
          this.applyMenuAndNavigate(program, menuItemstoDisplay);
        } else {
          // Config not found for this role — show error page
          this.router.navigate(['/unauthorized']);
        }
      });
      return;
    }

    // --- No role selected: load all menus unrestricted ---
    const menuItemstoDisplay = this.menuService.getAvailableMenus(program.program_code, []);
    this.applyMenuAndNavigate(program, menuItemstoDisplay);
  }

  private applyMenuAndNavigate(program: Program, menuItemstoDisplay: MainMenuItem): void {
    if (!environment.isExternal) {
      const basicMenus = menuItemstoDisplay[this.menuService.getLandingCategory(program.program_code)];
      const landingComponent = basicMenus && basicMenus.length > 0
        ? basicMenus[0].landing_component
        : 'participantEnrollmentList';
      if (landingComponent != 'globalConsumerSearch') {
        this.userService.setFirstSegment(landingComponent ?? 'participantEnrollmentList');
      }
      this.router.navigate([`/home/${landingComponent}`]);
      return;
    }
    this.handleExternalLandingPage(program, `/home/participantEnrollmentList`);
  }

  handleExternalLandingPage(program: Program, defaultRoute: string) {
    if (this.userData) {
      const selectedRole = this.userService.getSelectedRole();

      // get lookup post program selection 
      this.lookupService.fetchAndStoreLookupValues();

      switch (selectedRole?.role_name?.toLowerCase()) {
        case 'participant':
          this.userService.setFirstSegment(STATIC_DATA.PARTICIPANT_ENROLLMENT_LIST);
          this.getEnrollmentAndNavigate(STATIC_DATA.PARTICIPANT_ENROLLMENT, program.program_code, selectedRole?.referenceId);
          break;
        case 'independent facilitator':
        case 'if staff':
        case 'regional center':
        case 'regional center admin':
          this.userService.setFirstSegment(STATIC_DATA.PARTICIPANT_ENROLLMENT_LIST);
          this.router.navigate([defaultRoute]);
          break;
        default:
          this.userService.setFirstSegment(STATIC_DATA.PROVIDER_ENROLLMENT_LIST);
          if (selectedRole?.referenceId) {
            this.getEnrollmentAndNavigate(STATIC_DATA.PROVIDER_ENROLLMENT, program.program_code, selectedRole?.referenceId);
          }
          break;
      }
    }
  }

  // not used
  navigateToParticipant(referenceId: string, program_code: string) {
    this.userService.setLeadEntry('participantEnrollment');
    this.userService.setSelectedLead(referenceId);
    this.getEnrollmentAndNavigate(STATIC_DATA.PROVIDER_ENROLLMENT, program_code, referenceId)

  }

  // not used
  navigateToProvider(referenceId: string, program_code: string) {

    this.userService.setSelectedLead(referenceId);
    this.sectionService.setFlow('providerEnrollment');
    this.getEnrollmentAndNavigate(STATIC_DATA.PROVIDER_ENROLLMENT, program_code, referenceId);
  }

  getEnrollmentAndNavigate(entry: string, program_code: string, referenceId: string) {
    this.referralDataService.clearReferrralData();
    this.userService.setLeadEntry(entry);
    this.sectionService.setFlow(entry);
    this.userService.setSelectedLead(referenceId);
    this.leadDetailsService
      .fetchLeadDetailsApi(
        entry,
        program_code,
        Number(referenceId)
      )
      .subscribe((data) => {

        if (entry === STATIC_DATA.PROVIDER_ENROLLMENT) {
          this.savedData = data?.responsedata?.basicInfo;
          // keeing provider type as IR when there were no associaitons.
          const providerTypesList = (data?.responsedata?.participantAssociations ?? [data?.responsedata?.providerType || 'IR']).map((provider: any) => ENROLLMENT_TYPE_BACKEND_MAPPINGS[provider.providerType]).filter(Boolean);
          this.storageService.setProviderId(this.savedData?.providerId);
          this.storageService.setTrackingNumber(this.savedData?.trackingNumber);
          this.storageService.setProviderPPLId(this.savedData?.providerPPLId);
          this.sectionService.setEnrollmentType(JSON.stringify(providerTypesList));
        } else {
          this.savedData = data?.responsedata?.infoData;
        }

        if (entry === STATIC_DATA.PARTICIPANT_ENROLLMENT) {
          this.storageService.setProviderId(this.savedData?.providerId);
          this.storageService.setTrackingNumber(this.savedData?.trackingNumber);
          this.storageService.setProviderPPLId(this.savedData?.providerPPLId);
        }

        if (
          this.savedData?.applicationStatus === 'New Application' ||
          this.savedData?.applicationStatus === 'In Progress'
        ) {

          if (entry === STATIC_DATA.PROVIDER_ENROLLMENT) {

            

            this.referralDataService.setReferralDetails(data?.responsedata);
          }

          if (entry === STATIC_DATA.PARTICIPANT_ENROLLMENT) {
            this.referralDataService.setReferralDetails(data?.responsedata);
            this.storageService.setParticipantId(this.savedData?.participantId);
            this.storageService.setParticipantPPLId(this.savedData?.participantPPLId);
            this.storageService.setTrackingNumber(this.savedData?.trackingNumber);

          }

          setTimeout(() => {
            this.router.navigate(['/newReferral']);
          }, 200);
        } else {
          // External members use enrollmentDetails route — update firstSegment
          // so child components derive the correct EXTERNAL_MEMBER_VIEW pageId
          this.userService.setFirstSegment(MENU_BASE_URLS.EXTERNAL_MEMBERS);
          setTimeout(() => {
            this.router.navigate(['/home/enrollmentDetails']);
          }, 200);
        }
      });
  }
}
